import { Injectable, InjectionToken, Signal, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

/** Reads server feature flags once at startup. `enableVoice` gates all mic UI, so the
 *  whole voice feature can be toggled from the API's appsettings.json without a web rebuild. */
const VOICE_KEY = 'afs.config.enableVoice';

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private http = inject(HttpClient);
  // Seed from the last-known value so an OFFLINE load (config fetch fails) still shows the
  // mic — audio capture already queues offline and transcribes on reconnect.
  readonly enableVoice = signal(this.readPersisted());

  async load(): Promise<void> {
    try {
      const cfg = await firstValueFrom(
        this.http.get<{ enableVoice: boolean }>(`${environment.apiUrl}/api/config`));
      this.enableVoice.set(!!cfg.enableVoice);
      try { localStorage.setItem(VOICE_KEY, cfg.enableVoice ? '1' : '0'); } catch { /* ignore */ }
    } catch {
      // Config unreachable (offline) → keep the persisted last-known value seeded above.
    }
  }

  private readPersisted(): boolean {
    try { return localStorage.getItem(VOICE_KEY) === '1'; } catch { return false; }
  }
}

/** Injectable read-only view of the voice flag. Safe default `false` so merely rendering a
 *  mic component in a test (no HttpClient / no AppConfigService wiring) never explodes —
 *  mirrors the AUDIO_TRANSCRIBER safe-default pattern. app.config binds the real signal. */
export const VOICE_ENABLED = new InjectionToken<Signal<boolean>>('VOICE_ENABLED', {
  providedIn: 'root',
  factory: () => signal(false),
});
